import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { cn } from '../lib/utils';
import Input from '../components/Input';
import userService from '../services/userService';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Field from '../components/Field';
import adminDashboardService from '../services/adminDashboardService';

const ITEMS_PER_PAGE = 8;

const plans = [
  {
    label: 'All',
    value: '',
  },
  {
    label: 'Freemium',
    value: 'freemium',
  },
  {
    label: 'Admin',
    value: 'admin',
  },
];

const UsersPage = () => {
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [platforms, setPlatforms] = useState([{ label: 'All', value: '' }]);
  const [page, setPage] = useState(1);

  const { users_list_data, users_list_loading } = userService?.GetUsersList();

  const { platforms_data } = adminDashboardService?.GetPlatforms();

  useEffect(() => {
    if (platforms_data?.platforms) {
      const formattedPlatforms = platforms_data?.platforms?.map(p => {
        return {
          label: p.platform_id.charAt(0).toUpperCase() + p.platform_id.slice(1),
          value: p.platform_id,
        };
      });
      setPlatforms([{ label: 'All', value: '' }, ...formattedPlatforms]);
    }
  }, [platforms_data]);

  const filteredUsers = useMemo(() => {
    return users_list_data?.detail?.filter(user => {
      const fullName = `${user?.first_name} ${user?.last_name}`.toLowerCase();
      const matchesSearch =
        fullName.includes(search.toLowerCase()) || user?.email.toLowerCase().includes(search.toLowerCase());
      const matchesPlan = user?.plan?.type?.includes(planFilter);
      const matchesPlatform = user?.platform?.includes(platformFilter);
      return matchesSearch && matchesPlan && matchesPlatform;
    });
  }, [users_list_data?.detail, search, planFilter, platformFilter]);

  const totalPages = Math.ceil(filteredUsers?.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers?.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const getPlanBadgeStyle = plan => {
    switch (plan) {
      case 'enterprise':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'admin':
        return 'bg-[#721b1f]/10 text-[#721b1f] border border-[#721b1f]/20';
      default:
        return 'bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.8)] border border-[rgba(157,157,157,0.3)]';
    }
  };

  const getPaginationRange = (current, total) => {
    const delta = 2; // How many pages to show on each side of current
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage and monitor all platform users</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-3">
          <div className="relative flex flex-1 min-w-[240px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              className="!pl-10 bg-card border-border text-sm h-9"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <Field
            type="select"
            name="planType"
            placeholder="Select Plan"
            options={plans}
            className="w-[170px]"
            noMargin
            onChange={e => {
              setPlanFilter(e?.target?.value?.value);
            }}
          />
          <Field
            type="select"
            name="platform"
            placeholder="Select Platform"
            options={platforms}
            className="w-[170px]"
            noMargin
            onChange={e => {
              setPlatformFilter(e?.target?.value?.value);
            }}
          />
        </div>
        <span className="text-sm text-muted-foreground ml-auto">{filteredUsers?.length} users found</span>
      </div>

      <div className="bg-[#19191b] border border-[rgba(157,157,157,0.3)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[rgba(157,157,157,0.3)] bg-white/5">
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                  User
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                  Email
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                  Plan
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                  Platform
                </th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                  Requests
                </th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users_list_loading
                ? Array.from({ length: 8 }).map((_, index) => (
                    <tr key={`skeleton-${index}`} className="border-b border-[rgba(157,157,157,0.3)]">
                      {/* User Name & Avatar */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <Skeleton circle width={32} height={32} baseColor="#202020" highlightColor="#444" />
                          <Skeleton width={120} height={16} baseColor="#202020" highlightColor="#444" />
                        </div>
                      </td>

                      {/* Email */}
                      <td className="px-5 py-3.5">
                        <Skeleton width={180} height={14} baseColor="#202020" highlightColor="#444" />
                      </td>

                      {/* Plan */}
                      <td className="px-5 py-3.5">
                        <Skeleton width={60} height={20} borderRadius={4} baseColor="#202020" highlightColor="#444" />
                      </td>

                      {/* Platform */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <Skeleton width={20} height={20} baseColor="#202020" highlightColor="#444" />
                          <Skeleton width={80} height={14} baseColor="#202020" highlightColor="#444" />
                        </div>
                      </td>

                      {/* Total Requests */}
                      <td className="px-5 py-3.5 text-right">
                        <Skeleton width={50} height={14} baseColor="#202020" highlightColor="#444" />
                      </td>

                      {/* Action Link */}
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex justify-end">
                          <Skeleton width={100} height={14} baseColor="#202020" highlightColor="#444" />
                        </div>
                      </td>
                    </tr>
                  ))
                : paginatedUsers.map(user => {
                    return (
                      <tr
                        key={user?.id}
                        className="border-b border-[rgba(157,157,157,0.3)] hover:bg-white/5 transition-colors">
                        <td className="px-5 py-3.5">
                          <Link to={`/users/${user?.id}`} className="flex items-center gap-3 group">
                            <div className="w-8 h-8 rounded-full bg-[#721b1f] capitalize flex items-center justify-center text-xs font-semibold text-white shrink-0 transition-colors">
                              {user?.first_name?.slice(0, 1)}
                            </div>
                            <p className="text-sm font-medium text-ink capitalize group-hover:text-[#721b1f] transition-colors">
                              {user?.first_name} {user?.last_name}
                            </p>
                          </Link>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-sm text-muted-foreground">{user?.email}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div
                            className={cn(
                              'text-[11px] font-medium capitalize px-2 rounded-xl w-max',
                              getPlanBadgeStyle(user?.plan?.type),
                            )}>
                            {user?.plan?.type}
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-sm text-foreground flex items-center gap-1.5">
                            {/* <span className="text-base leading-none">{platform?.icon}</span> */}
                            {user?.platform}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <span className="text-sm font-medium text-foreground tabular-nums">
                            {user?.usage?.api_calls ?? 0}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          {/* <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem asChild>
                              <Link to={`/users/${user?.id}`} className="gap-2">
                                <Eye className="w-4 h-4" /> View Details
                              </Link>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu> */}
                          <Link
                            to={`/users/${user?.id}`}
                            className="flex items-center justify-end gap-2 text-ink text-sm hover:text-[#721b1f]">
                            <Eye className="w-4 h-4" /> View Details
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-[rgba(157,157,157,0.3)]">
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {getPaginationRange(page, totalPages).map((p, index) => {
              if (p === '...') {
                return (
                  <span key={`dots-${index}`} className="px-2 text-muted-foreground text-xs">
                    ...
                  </span>
                );
              }

              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={cn(
                    'w-7 h-7 rounded-md text-xs font-medium transition-colors',
                    p === page
                      ? 'bg-[#721b1f] text-white'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                  )}>
                  {p}
                </button>
              );
            })}
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersPage;
